import { useCallback, useLayoutEffect, useMemo, useState } from "react";

/**
 * Draft state for the list-shaped profile sections (PMH, allergies,
 * medications, immunizations, social and family history rows). A draft row
 * keeps the server item's `id`/`version` so a save supersedes the right head,
 * and holds every editable value as a string or boolean so inputs stay
 * controlled. Rows dropped from the draft are simply absent from the save and
 * the replace-set writer inactivates them.
 *
 * Reset is keyed on `resetKey` (patient + section + edit session), never on
 * the initial rows themselves: a background refetch of the same chart must
 * not wipe what the clinician is typing (see the rtl-late-passive-effect
 * note in the project memory for why this is a layout effect).
 */

export type FieldKind = "text" | "textarea" | "select" | "number" | "date" | "checkbox";

export interface FieldOption {
  readonly value: string;
  readonly label: string;
}

export interface FieldSpec {
  readonly key: string;
  readonly label: string;
  readonly kind: FieldKind;
  readonly options?: readonly FieldOption[];
  readonly required?: boolean;
  readonly placeholder?: string;
  /** Short value (a year, a code, a status): takes a narrow column. */
  readonly narrow?: boolean;
  /** Free text: spans the row. */
  readonly wide?: boolean;
  /**
   * Optional detail kept out of the way: fields sharing this label render
   * together in a closed disclosure under the row (the allergy and
   * medication codes).
   */
  readonly disclosure?: string;
  readonly min?: number;
  readonly max?: number;
  /** Longest text the input takes (a two-letter code). */
  readonly maxLength?: number;
}

export type FieldValue = string | boolean;

export interface RowDraft {
  readonly clientKey: string;
  readonly id?: string;
  readonly version?: number;
  /** Editable values plus hidden pass-through values (keys outside the specs). */
  readonly fields: Readonly<Record<string, FieldValue>>;
}

export function newClientKey(): string {
  return typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : Math.random().toString(36).slice(2);
}

export function emptyDraft(
  specs: readonly FieldSpec[],
  defaults: Readonly<Record<string, FieldValue>> = {},
): RowDraft {
  const fields: Record<string, FieldValue> = {};
  for (const spec of specs) {
    fields[spec.key] =
      defaults[spec.key] ?? (spec.kind === "checkbox" ? false : (spec.options?.[0]?.value ?? ""));
  }
  return { clientKey: newClientKey(), fields: { ...fields, ...defaults } };
}

/** A draft from a server item; `extra` carries derived or hidden values. */
export function draftFromItem(
  specs: readonly FieldSpec[],
  item: Readonly<Record<string, unknown>>,
  extra: Readonly<Record<string, FieldValue>> = {},
): RowDraft {
  const fields: Record<string, FieldValue> = {};
  for (const spec of specs) {
    const value = item[spec.key];
    fields[spec.key] =
      spec.kind === "checkbox"
        ? value === true
        : value === undefined || value === null
          ? ""
          : String(value);
  }
  const id = typeof item.id === "string" ? item.id : undefined;
  const version = typeof item.version === "number" ? item.version : undefined;
  return {
    clientKey: id ?? newClientKey(),
    ...(id === undefined ? {} : { id }),
    ...(version === undefined ? {} : { version }),
    fields: { ...fields, ...extra },
  };
}

export function fieldString(row: RowDraft, key: string): string {
  const value = row.fields[key];
  return typeof value === "string" ? value.trim() : "";
}

export function fieldBoolean(row: RowDraft, key: string): boolean {
  return row.fields[key] === true;
}

/** `id`/`version` pass-through for an existing row, nothing for a new one. */
export function rowIdentity(row: RowDraft): { id?: string; version?: number } {
  return {
    ...(row.id === undefined ? {} : { id: row.id }),
    ...(row.version === undefined ? {} : { version: row.version }),
  };
}

/** A string field only when it holds text, so strict write schemas never see "". */
export function optionalText(row: RowDraft, key: string): Record<string, string> {
  const value = fieldString(row, key);
  return value.length > 0 ? { [key]: value } : {};
}

export function optionalNumber(row: RowDraft, key: string): Record<string, number> {
  const value = fieldString(row, key);
  if (value.length === 0) return {};
  const parsed = Number(value);
  return Number.isFinite(parsed) ? { [key]: parsed } : {};
}

export interface RowDraftsApi {
  readonly rows: readonly RowDraft[];
  readonly dirty: boolean;
  update(clientKey: string, key: string, value: FieldValue): void;
  add(row: RowDraft): void;
  remove(clientKey: string): void;
}

export function useRowDrafts({
  initial,
  resetKey,
  toItem,
}: {
  /** Memoize on `resetKey`; the hook reads it when the key changes. */
  readonly initial: readonly RowDraft[];
  readonly resetKey: string;
  /** The save shape of a row; the dirty check compares these, not raw fields. */
  readonly toItem: (row: RowDraft) => unknown;
}): RowDraftsApi {
  const [rows, setRows] = useState<readonly RowDraft[]>(initial);

  // biome-ignore lint/correctness/useExhaustiveDependencies: resetKey is the intentional trigger; `initial` changes on every refetch and must not reset in-progress drafts.
  useLayoutEffect(() => {
    setRows(initial);
  }, [resetKey]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: the baseline belongs to the edit session, not to the live initial rows.
  const baseline = useMemo(() => JSON.stringify(initial.map(toItem)), [resetKey]);
  const dirty = JSON.stringify(rows.map(toItem)) !== baseline;

  const update = useCallback((clientKey: string, key: string, value: FieldValue) => {
    setRows((current) =>
      current.map((row) =>
        row.clientKey === clientKey ? { ...row, fields: { ...row.fields, [key]: value } } : row,
      ),
    );
  }, []);
  const add = useCallback((row: RowDraft) => {
    setRows((current) => [...current, row]);
  }, []);
  const remove = useCallback((clientKey: string) => {
    setRows((current) => current.filter((row) => row.clientKey !== clientKey));
  }, []);

  return { rows, dirty, update, add, remove };
}
